import * as React from 'react'
import {useParams} from 'react-router'
import {fromJust} from '@freckle/maybe'
import useSWR from 'swr'

import {
  fetchTeacherLicenseAllocations,
  postTeacherLicenseAllocation
} from '@frontrow/console/src/helpers/api-teachers'
import {
  type TeacherLicenseAllocationT,
  type TeacherLicenseAllocationPostT,
  parseLicenses
} from '@frontrow/console/src/models/teacher-license-allocation'
import {firstResponseApiError} from '@frontrow/console/src/entities/models/api-error'
import TeacherLicenses from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses/teacher-licenses'
import LicenseRow from '@frontrow/console/src/components/pages/teachers-page/teacher-licenses/license-row'

const TEACHER_LICENSES_SWR_KEY = 'teacher-licenses'

// #region Methods
function getRenderListElement(deps: {
  mutateTeacherLicenses: () => void
}): (license: TeacherLicenseAllocationT, index: number) => React.ReactElement {
  const {mutateTeacherLicenses} = deps
  function ListElement(license: TeacherLicenseAllocationT, index: number): React.ReactElement {
    return (
      <LicenseRow key={index} license={license} mutateTeacherLicenses={mutateTeacherLicenses} />
    )
  }
  return ListElement
}

function getOnAddLicense(deps: {setAddingLicense: (a: boolean) => void}): () => void {
  const {setAddingLicense} = deps
  return () => setAddingLicense(true)
}

function getOnCreateLicense(deps: {
  teacherId: number
  createTeacherLicense: (
    teacherId: number,
    newLicense: TeacherLicenseAllocationPostT
  ) => Promise<void>
  setAddingLicense: (a: boolean) => void
  setCreateError: (a: string | null) => void
}): (newLicense: TeacherLicenseAllocationPostT) => void {
  const {teacherId, createTeacherLicense, setAddingLicense, setCreateError} = deps
  // The call used to be fired and forgotten, so a rejected POST became an
  // unhandled rejection and the form closed as if it had worked. Waiting for it
  // is what lets the failure reach the page and the form stay open.
  return (newLicense: TeacherLicenseAllocationPostT) => {
    setCreateError(null)
    createTeacherLicense(teacherId, newLicense)
      .then(() => setAddingLicense(false))
      .catch((error: unknown) =>
        setCreateError(error instanceof Error ? error.message : 'Could not add the license.')
      )
  }
}
// #endregion

export function TeacherLicensesContainer(): React.ReactElement {
  const [addingLicense, setAddingLicense] = React.useState(false)
  const [createError, setCreateError] = React.useState<string | null>(null)
  const params = useParams<{teacherId: string}>()
  const teacherId = parseInt(fromJust(params.teacherId, 'Cannot find teacherId in URL'), 10)

  const {
    data,
    error,
    isLoading,
    mutate: mutateTeacherLicenses
  } = useSWR([TEACHER_LICENSES_SWR_KEY, teacherId], async ([_, id]) => {
    const response = await fetchTeacherLicenseAllocations(id)
    return parseLicenses(response)
  })

  const createTeacherLicense = React.useCallback(
    async (teacherId: number, newLicense: TeacherLicenseAllocationPostT): Promise<void> => {
      try {
        await postTeacherLicenseAllocation(teacherId, newLicense)

        // Revalidate to sync with server
        mutateTeacherLicenses()
      } catch (error: unknown) {
        const apiError = firstResponseApiError(error)
        throw new Error(apiError.message)
      }
    },
    [mutateTeacherLicenses]
  )

  // Latest expiry first, which sinks expired licenses to the bottom on its
  // own. The endpoint returns them in no particular order.
  const licenses = React.useMemo(
    () => [...(data ?? [])].sort((a, b) => b.expiresAt.getTime() - a.expiresAt.getTime()),
    [data]
  )

  const renderListElement = getRenderListElement({mutateTeacherLicenses})
  const onAddLicense = getOnAddLicense({setAddingLicense})
  const onCreateLicense = getOnCreateLicense({
    teacherId,
    createTeacherLicense,
    setAddingLicense,
    setCreateError
  })

  const onCancelAddLicense = () => {
    setCreateError(null)
    setAddingLicense(false)
  }

  return (
    <TeacherLicenses
      teacherId={teacherId}
      licenses={licenses}
      error={error?.message ?? createError}
      isLoading={isLoading}
      addingLicense={addingLicense}
      renderListElement={renderListElement}
      onAddLicense={onAddLicense}
      onCancelAddLicense={onCancelAddLicense}
      onCreateLicense={onCreateLicense}
    />
  )
}
